import styles from './index.module.scss'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import InsuranceFilter from 'app/ProviderSearch/HeaderFilter/InsuranceFilter'
import AreaOfConcernFilter from 'app/ProviderSearch/HeaderFilter/AreaOfConcernFilter'
import LocationFilter from 'app/ProviderSearch/HeaderFilter/LocationFilter'
import omit from 'lodash/omit'
import { useRouter } from 'next/router'
import { getParsedQuery, modifyURLQuery } from 'utils/common'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { CounselingTypeEnum } from 'app/Bookings/constants'
import { locationKeysToStore } from 'app/ProviderSearch/HeaderFilter/constants'
import { PAGINATION_LIMIT } from 'constants/api'
import B2BClientFilter from './B2BClientFilter'
import useThriveAuth from 'hooks/useThriveAuth'
import ScrollHorizontally from 'common/ScrollHorizontally'

type HeaderFilterProps = {
  groupedFilters: GroupedProviderFilter
}

const HeaderFilter: React.FC<HeaderFilterProps> = ({ groupedFilters }) => {
  const [hideOtherFilters, setHideOtherFilters] = useState<boolean>(false)
  const [isAdhdFilterSelected, setAdhdFilterSelected] = useState<boolean>(false)

  const router = useRouter()
  const { isSupportUser } = useThriveAuth()
  const allAreasOfConcern = groupedFilters[ProviderFilterTypes.AREA_OF_CONCERN]

  const setToSessionStorage = (value: Record<string, unknown>) => {
    locationKeysToStore.map((key) => {
      sessionStorage.setItem(key, value[key] as string)
    })
  }

  const checkIsAdhdFilterSelected = (areaOfConcernFilter: string[]) => {
    const adhdFilterObj = allAreasOfConcern.find((aoc) => aoc.value === 'ADHD')

    return areaOfConcernFilter?.includes(adhdFilterObj.id)
  }

  useEffect(() => {
    const { areaOfConcern } = getParsedQuery(router)
    const IsAdhdFilterSelected = checkIsAdhdFilterSelected(areaOfConcern)

    setAdhdFilterSelected(IsAdhdFilterSelected)
  }, [])

  const onFilterSelect = (selectedFilter: Record<string, unknown>) => {
    const parsedQuery = getParsedQuery(router)
    const { partnershipCode } = parsedQuery ?? {}

    const IsAdhdFilterSelected = checkIsAdhdFilterSelected(
      selectedFilter?.areaOfConcern as string[]
    )

    setAdhdFilterSelected(IsAdhdFilterSelected)

    // When location filter is selected
    if (selectedFilter.location) {
      const updatedQuery = {
        ...selectedFilter,
        offset: '0',
        limit: PAGINATION_LIMIT.toString(),
      }
      // Change the counseling type to individual counseling when the location changes
      const individualCounselingFilter = groupedFilters[
        ProviderFilterTypes.COUNSELING_TYPE
      ]?.find((filter) => filter.value === CounselingTypeEnum.INDIVIDUAL)

      if (individualCounselingFilter) {
        updatedQuery['counselingType'] = [individualCounselingFilter.id]
      }

      if (!!partnershipCode) {
        updatedQuery['partnershipCode'] = partnershipCode
      }

      // Only sets - state,lat,long,location to the sessionStorage
      setToSessionStorage(selectedFilter)
      // Remove rest of the filter and only apply location filters
      modifyURLQuery(router, updatedQuery, true)
    } else {
      // When speciality and insurance filter is selected.
      const updatedQuery = omit(parsedQuery, Object.keys(selectedFilter))
      updatedQuery['offset'] = 0

      modifyURLQuery(router, { ...updatedQuery, ...selectedFilter })
    }
  }

  const renderHeaderFilterSection = (): JSX.Element => (
    <div className={styles.header_filter_section}>
      <div className={styles.header_filter_items_wrapper}>
        <div className={styles.header_filter_container}>
          {!hideOtherFilters && isSupportUser && (
            <>
              <B2BClientFilter />
              <div className={styles.header_divider} />
            </>
          )}
          <LocationFilter
            setHideOtherFilters={setHideOtherFilters}
            onFilterSelect={onFilterSelect}
            containerClassName={classNames(styles.location_filter_item, {
              [styles.occupy_full_width]: hideOtherFilters,
            })}
            isB2bClientFilter={isSupportUser}
          />
          {!hideOtherFilters && (
            <>
              <div className={styles.header_divider} />
              <AreaOfConcernFilter
                onFilterSelect={onFilterSelect}
                groupedFilters={groupedFilters}
                containerClassName={styles.header_filter_items}
                isAdhdFilterSelected={isAdhdFilterSelected}
              />
              <div className={styles.header_divider} />
              <InsuranceFilter
                onFilterSelect={onFilterSelect}
                containerClassName={styles.header_filter_items}
              />
            </>
          )}
        </div>
      </div>
      {/* This is requried to push the header filter to the right side a bit because it has empty space on the right */}
      <div className={styles.header_filler_space} />
    </div>
  )

  /*
    Added scroll for header filter in case of support user to fix overflow in mobile view
    due to partnership client filter
  */
  if (isSupportUser) {
    return (
      <ScrollHorizontally
        containerClass={styles.scroll_container}
        childrenClass={styles.scroll_container}
      >
        {renderHeaderFilterSection()}
      </ScrollHorizontally>
    )
  }

  return renderHeaderFilterSection()
}

export default HeaderFilter
